import { useEffect, useMemo, useState } from 'react';
import { fetchAiUsageSummary, type AiUsageSummary } from '../../api/assist-conversations';
import { getNumberLocale } from '../../lib/base-i18n';

/**
 * AI 레일 바닥 — **내가 얼마나 썼나** (판 L · 판 M · 시안 버전 7).
 *
 * 2026-09-10 사용자 지적: 「AI 메뉴 하위메뉴 제일 아래에 사용량이 표기가 안 된다」.
 * 실측하니 8443 에는 사용량을 그리는 코드가 **아예 없었다** — 관문 G1 의 숫자는
 * 아무도 못 여는 `/system-admin/audit` 에만 있었다.
 *
 * ## ★★★ 「0」과 「모른다」를 가른다
 *
 * 못 읽었을 때 0 으로 그리면 **「안 썼다」는 거짓**이 된다. 계량은 provider 가 토큰 수를
 * 알려 줬을 때만 쌓이므로, 여기 숫자는 «잰 것의 합»이지 «전부»가 아니다 — 화면이 그렇게 적는다.
 *
 * ## ★★★ 판 L — «내» 것이다
 *
 * 판 K 에서는 회사 총량이었다. 「회사 관리자, 사용자 모두 AI 메뉴에는 **개인 사용량**이
 * 나와야 한다」(2026-09-10 사용자)로 바뀌었다 — 회사 총량은 통합설정(회사 관리자)의 몫이고,
 * 여기서 회사 숫자를 보이면 사람들이 **제가 쓴 양으로 오해한다.**
 *
 * ## ★★★ 2026-09-12 사용자 지적: 「좌측 메뉴의 프로그레스바가 안 나온다」 — 규칙을 바꾼다
 *
 * 라이브로 원인을 확정했다. 응답은 멀쩡하고 **`quota.limit` 이 `null`**(`source: 'none'`)이었다 —
 * 이 회사는 `institutions.ai_member_default_limit` 도 `user_ai_quotas` 줄도 없다. 그래서 종전
 * 규칙(「무제한이면 띠를 안 그린다」)대로 **일반 사용자·관리자 둘 다** 띠가 없었다.
 * (통합설정 › 회사 관리 › AI 한도에 「사원 기본」을 넣으면 그 즉시 진짜 띠가 나온다 — 실측했다.)
 *
 * **이제 한도가 없어도 띠를 그린다.** 다만 «무엇에 대한 띠인지»를 숨기지 않는다:
 *
 * | 판 | 눈금 | 화면이 하는 말 |
 * |---|---|---|
 * | 한도 있음 | 한도 | 「쓴 양 / 한도」 + 띠 + 「N% 썼습니다 · 남은 M」 |
 * | 한도 없음 | **최근 석 달 월 최고치** | 「쓴 양 / 최고치」 + 띠 + 「최고치의 N%」 + 「한도 없음」 |
 * | 넘음 | 한도 | 띠가 danger 로 차고 **글로도** 「한도를 넘었습니다」 |
 * | 아직 없음 | — | 띠를 안 그린다. **눈금이 없으면 띠는 거짓말이다** — 0 을 100% 로 적지 않는다 |
 * | 못 읽음 | — | 한도 줄을 지우고 **쓴 양만**. 「안 썼다」와 「못 읽었다」는 다른 사실이다 |
 *
 * ★★★ 마지막 판이 특히 중요하다 — 서버 라우트가 일부러 `readMyQuota(...).catch(() => null)`
 *    로 사용량과 갈라 두었다(「한도 조회가 실패해도 사용량은 보여 준다」). 화면이 그 갈래를
 *    안 그리면 그 배려가 통째로 사라진다.
 *
 * ★★ **눈금은 한도가 아니다** — 그래서 채움도 색이 다르고(`is-soft`) 라벨이 기준을 적는다.
 *    한도를 다 썼다는 뜻으로 읽히면 안 된다. 「이 달이 가장 많습니다」는 «넘었다»가 아니다.
 *
 * ★★ 넘어도 **막지 않는다** — 계량이 CLI 를 못 재기 때문(2026-09-10 결정). 화면은 말만 한다.
 */

/** `YYYY-MM` 을 n 달 뒤로 민다(음수면 앞으로). 서버가 준 달을 기준으로 센다. */
function shiftMonth(month: string, delta: number): string {
  const y = Number(month.slice(0, 4));
  const m = Number(month.slice(5, 7));
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return d.toISOString().slice(0, 7);
}

/**
 * 최근 석 달(이 달 + 앞선 두 달)을 덮는 **날수**. 서버는 「며칠」만 받는다(`?days=`).
 * ★ UTC 로 센다 — 사용량도 한도도 UTC 달을 쓴다(`ai_connection_usage.day`).
 */
export function daysCoveringThreeMonths(now: Date = new Date()): number {
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const from = Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 2, 1);
  return Math.floor((today - from) / 86_400_000) + 1;
}

/**
 * 한도가 없을 때 쓸 **눈금** — 최근 석 달의 «월 최고치».
 *
 * ★ 이 달 쓴 양이 **하한**이다. 자료가 어긋나도(창이 짧거나 series 가 비어도) 채움이
 *   100% 를 넘지 않고, 「이 달이 최고」라는 말이 참이 된다.
 * ★ 서버가 준 `month` 를 기준으로 창을 자른다 — 브라우저 시계가 달을 넘겨도 안 흔들린다.
 */
export function monthlyPeak(
  series: ReadonlyArray<{ bucket: string; tokens: number }>,
  month: string,
  usedThisMonth: number,
): number {
  const window = new Set([month, shiftMonth(month, -1), shiftMonth(month, -2)]);
  const byMonth = new Map<string, number>();
  for (const s of series) {
    const m = s.bucket.slice(0, 7);
    if (!window.has(m)) continue;
    byMonth.set(m, (byMonth.get(m) ?? 0) + s.tokens);
  }
  let peak = usedThisMonth;
  for (const v of byMonth.values()) if (v > peak) peak = v;
  return peak;
}

export function AiUsageFoot() {
  const [state, setState] = useState<'loading' | 'error' | 'ok'>('loading');
  const [usage, setUsage] = useState<AiUsageSummary | null>(null);
  /* 한 번 정하고 안 바꾼다 — 자정을 넘겨도 띠의 눈금이 화면 안에서 흔들리지 않게. */
  const span = useMemo(() => daysCoveringThreeMonths(), []);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const u = await fetchAiUsageSummary(span);
        if (!alive) return;
        setUsage(u);
        setState('ok');
      } catch {
        if (alive) setState('error');
      }
    })();
    return () => { alive = false; };
  }, [span]);

  /* ⚠ 로케일을 못 박지 않는다 — 이 레포의 가드가 `toLocale*` 의 하드코딩 BCP-47 을 막는다. */
  const fmt = (n: number): string => n.toLocaleString(getNumberLocale());

  /* ⚠ `!= null` 이다 — 서버가 칸을 아예 안 실으면 `undefined` 라 `!== null` 은 통과하고
        바로 아래 `.limit` 에서 화면이 죽는다(검사가 그것을 잡았다). */
  const quota = usage?.quota != null ? usage.quota : null;
  const hasLimit = quota !== null && quota.limit !== null;
  const percent = Math.min(100, quota?.percent ?? 0);

  /* 한도가 없을 때의 눈금 — 0 이면 «잴 것이 없다»라 띠를 안 그린다. */
  const peak = quota !== null && !hasLimit
    ? monthlyPeak(usage?.series ?? [], quota.month, quota.usedThisMonth)
    : 0;
  const hasScale = quota !== null && !hasLimit && peak > 0;
  /*
   * ★★★ **「이 달이 최고인가」는 «표시용 수»가 아니라 값으로 판정한다** (2026-09-12 리뷰 #1170).
   *    아래 `softPct` 는 띠 폭을 내는 «보여 줄 수»다. 그것으로 사실을 판정하면 이 달 99,600 ·
   *    지난달 100,000 일 때 **99.6 이 100 이 되어** 글도 · 분모 숨김도 · 낭독 이름도 한꺼번에
   *    거짓이 된다. 사실과 표시를 갈라 둔다.
   */
  const isPeak = hasScale && quota!.usedThisMonth >= peak;
  /*
   * ⚠ **내림이다.** 반올림하면 가득 차지도 않은 띠가 「100%」라고 적힌다 —
   *   그러면 위의 갈라 둠이 화면에서 다시 합쳐진다(띠는 가득인데 최고는 아닌 판).
   *   내림이면 `softPct === 100` 과 `isPeak` 이 같은 자리를 가리킨다.
   */
  const softPct = peak > 0 ? Math.min(100, Math.floor((quota!.usedThisMonth / peak) * 100)) : 0;

  return (
    <div className="ainav__foot">
      <span className="ainav__footttl">
        내 사용량
        {state === 'ok' && (
          <span className="ainav__footperiod">
            {quota !== null ? '이 달' : (usage?.days === span ? '최근 석 달' : `최근 ${usage?.days ?? span}일`)}
          </span>
        )}
      </span>

      {state === 'loading' && <span className="ainav__footv">읽는 중…</span>}
      {/* ★ 못 읽은 것을 0 으로 그리지 않는다. */}
      {state === 'error' && <span className="ainav__footv">지금은 읽지 못했습니다</span>}

      {state === 'ok' && usage !== null && (
        <>
          {hasLimit ? (
            <>
              {/* ① 정상·넘음 — 「쓴 양 / 한도」가 주인공이고 띠가 그 아래에 깔린다. */}
              <span className="ainav__footof">
                <b className="ainav__footused">{fmt(quota!.usedThisMonth)}</b>
                <span className="ainav__footlim">/ {fmt(Number(quota!.limit))}</span>
              </span>
              <span
                className="ainav__qtrack"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={percent}
                /* ★ 띠는 100 에서 멈추지만 «읽어 주는 값»은 참말이어야 한다 — 117% 를 100 으로 말하지 않는다. */
                aria-valuetext={`${quota!.percent ?? 0}%${quota!.over ? ' · 한도를 넘었습니다' : ''}`}
                aria-label={`이 달 사용량 ${fmt(quota!.usedThisMonth)} / 한도 ${fmt(Number(quota!.limit))}`}
              >
                <span
                  className={`ainav__qfill${quota!.over ? ' is-over' : ''}`}
                  style={{ width: `${percent}%` }}
                />
              </span>
              <span className="ainav__footrow">
                <span><b>{quota!.percent ?? 0}%</b> 썼습니다</span>
                {/* ★ 색만으로 알리지 않는다 — 넘었다는 말을 글로도 적는다. */}
                <span className={quota!.over ? 'is-over' : undefined}>
                  {quota!.over ? '한도를 넘었습니다' : `남은 ${fmt(quota!.remaining ?? 0)}`}
                </span>
              </span>
            </>
          ) : hasScale ? (
            <>
              {/*
                ② 한도 없음 — 띠는 그린다(2026-09-12 사용자 지시). 눈금이 «한도»가 아니라
                «최근 석 달 월 최고치»라는 것을 숫자 옆·아래에 그대로 적는다.
              */}
              {/*
                ★ **이 달이 «최고»면 분모를 안 적는다** (2026-09-12 리뷰 지적).
                  「46,951 / 46,951」이 나란히 서면 눈이 먼저 «한도를 다 썼다»로 읽는다 —
                  글로 아무리 「가장 많습니다」라고 적어도 숫자가 먼저 거짓말을 한다.
                  그리고 그 분모는 **이 달 쓴 양 바로 그 수**라 한 번 더 적을 값도 아니다.
                  한도가 «있는» 판은 그대로다 — 거기서는 분모가 진짜 한도다.
                ⚠ 판정은 `softPct` 가 아니라 `isPeak` 이다 — 최고에 아슬하게 못 미치는 판에서
                  **그 분모가 「다 썼다」를 스스로 반박하는 유일한 숫자**이기 때문이다.
              */}
              <span className="ainav__footof">
                <b className="ainav__footused">{fmt(quota!.usedThisMonth)}</b>
                {!isPeak && <span className="ainav__footlim">/ {fmt(peak)}</span>}
              </span>
              <span
                className="ainav__qtrack"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={softPct}
                aria-valuetext={`최근 석 달 최고치의 ${softPct}% · 한도 없음`}
                aria-label={isPeak
                  ? `이 달 사용량 ${fmt(quota!.usedThisMonth)} · 최근 석 달 중 가장 많습니다`
                  : `이 달 사용량 ${fmt(quota!.usedThisMonth)} · 눈금은 최근 석 달 월 최고치 ${fmt(peak)}`}
              >
                <span className="ainav__qfill is-soft" style={{ width: `${softPct}%` }} />
              </span>
              <span className="ainav__footrow">
                {/* ★ 100% 는 «다 썼다»가 아니라 «이 달이 가장 많다»다 — 그렇게 적는다. */}
                <span>
                  {isPeak ? '이 달이 가장 많습니다' : <>최고치의 <b>{softPct}%</b></>}
                </span>
                <span>한도 없음</span>
              </span>
              <span className="ainav__footnote">눈금은 한도가 아니라 최근 석 달 월 최고치입니다</span>
            </>
          ) : (
            <>
              {/*
                ③ 아직 없음 · ④ 못 읽음 — 둘 다 띠가 없다. 큰 수치가 하나뿐이라 등폭 숫자를 쓰지 않는다.
                ★ 띠를 안 그리는 이유가 서로 다르다: ③ 은 «눈금이 없어서»(석 달 내내 0), ④ 는
                  «한도를 못 읽어서»다. 글이 그 둘을 가른다.
              */}
              <span className="ainav__foothero">
                {fmt(quota !== null ? quota.usedThisMonth : usage.periodTokens)}
              </span>
              <span className="ainav__footnote">
                {quota !== null
                  ? <>한도 <b>없음</b> — 최근 석 달 동안 쓴 기록이 없습니다</>
                  : '한도를 읽지 못했습니다 · 쓴 양만'}
              </span>
            </>
          )}

          {/*
            바닥 한 줄 — 오늘치·기간 합·호출 수는 여기 남는다. 그리고 끝에 「잰 것만」.
            ★ `usedThisMonth` 주석이 「잰 것의 합이지 전부가 아니다」라고 적었다. 화면이 그 사실을 숨기면 안 된다.
            ⚠ 한도를 못 읽은 판에서는 **기간 합이 바로 위 큰 수치로 이미 나와 있다** — 같은 수를
              한 상자 안에 두 번 적지 않는다(326 폭에서는 그게 곧 소음이다).
            ⚠ 창이 «석 달»이 됐으므로 그렇게 적는다 — 서버가 다른 날수로 잘랐으면 그 날수를 적는다.
          */}
          <span className="ainav__foots">
            오늘 {fmt(usage.todayTokens)}
            {quota !== null && (
              <> · {usage.days === span ? '최근 석 달' : `최근 ${usage.days}일`} {fmt(usage.periodTokens)}</>
            )}
            {' · 호출 '}{fmt(usage.calls)}회 · 잰 것만
          </span>
        </>
      )}
    </div>
  );
}
